/**
 * „Do przejrzenia" — every submission waiting for the trainer's eye.
 *
 * ── The open submission's own answers are NOT here any more ──────────────────────────────────
 * `answerSheet` projected `GET /survey/assignment/{id}` into a flat list for a right-hand pane, and
 * that pane went on 17 September 2026: the owner asked for the preview to be the CLIENT DETAIL's
 * report, opened in a modal, so `features/survey-report` draws it from the client's own history.
 * Two renderings of one submission — one with deltas, units and media, one with none of the three —
 * is the duplication that rule exists to stop, and this was the poorer of the two.
 *
 * ── The window is the RESPONSE's, never a constant of ours ───────────────────────────────────
 * `GET /survey/review-queue` answers with `windowDays` beside its items, and the sentence under the
 * list reads that field. Thirty days is the backend's figure today (`SurveyProcessor`'s
 * `ReviewQueueWindowDays`); writing it here would put a second copy of it on the wrong side of the
 * wire, and the day it changes the screen would state the old one with total confidence. The same
 * goes for `isTruncated`: the read says whether it cut the list, so the list says so too.
 *
 * ── What the backend already guarantees, and what this module guards anyway ──────────────────
 * The query returns SUBMITTED and still-unreviewed assignments only, newest first, capped at three
 * summary answers, with media, structured answers and health-data answers left out of the preview.
 * This module does not re-derive any of that. What it does refuse is a row it cannot draw: orval
 * types every field optional, so an item with no assignment id has nothing to open and nothing to
 * mark, and an item with no submission instant cannot be said to have been submitted at all. Both
 * are dropped rather than rendered as a row with holes in it.
 *
 * ── `person` is nullable and a name is never blank ───────────────────────────────────────────
 * The backend leaves `Person` absent when the directory cannot name the account behind a roster
 * row, and says in the DTO that the reader renders its own stand-in. „Klient bez nazwy" is that
 * stand-in — the row still says which client sent it, and a blank cell would read as a bug.
 *
 * ── The date is the OCCURRENCE's for a check-in and the SUBMISSION's for an intake ───────────
 * A check-in belongs to a cycle („5 paź", with „2 dni po terminie" under it when it came late),
 * while an intake has no cycle at all and is dated by when it arrived („14 paź"). The inbox's row
 * prints the day alone (owner, 4 November 2026: no weekday, no clock); the pane's meta line says
 * both days — the cycle's and the filling's. `cycleDate` is a civil date and is read verbatim as UTC midnight;
 * `submittedAt` is an instant and is worded in the reader's own zone. Mixing the two is how one
 * surface comes to disagree with another about which day something happened.
 *
 * No React, no DOM, no query hook, no i18next: `t` and the locale arrive as parameters.
 */

import type {
  TrainWithMeModulesSurveysDtosResponsesSurveyAnswerSummaryItem as SummaryItem,
  TrainWithMeModulesSurveysDtosResponsesSurveyReviewQueueItem as QueueItem,
  TrainWithMeModulesSurveysDtosResponsesSurveyReviewQueueResponse as QueueResponse,
} from '~/shared/api/generated/model';
import {
  hasStandardOptionLabels,
  questionNames,
  standardOptionLabel,
} from '~/shared/lib/standardQuestionNames';

import { daysLate, latenessLabel } from '~/features/survey-report';

import type { TranslateKey } from './surveyList';

/** One row in the queue. */
export interface ReviewRow {
  readonly assignmentId: number;
  readonly customerId: number;
  /** Never blank — see the header. */
  readonly name: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly templateTitle: string;
  /** The form's short kind for the row — „Check-in" or „Wstępna". */
  readonly formShort: string;
  /** „Waga 61,4 kg · sen 3/5 · energia 2/5". Empty when the submission previews nothing. */
  readonly summary: string;
  /** „5 paź": the cycle's day for a check-in, the arrival's for an intake. */
  readonly when: string;
  /** „2 dni po terminie", or `null` for a submission that came on time. */
  readonly lateness: string | null;
  /** Late, as the backend stamped it. `lateness` carries the meaning in words; this only marks. */
  readonly late: boolean;
  /**
   * A CHECK-IN that came in on time — the row's ember dot and its „na czas" for a listener. False for
   * an intake: it has no term to keep, so it is neither on time nor late.
   */
  readonly onTime: boolean;
  /** The pane's line under the name: „Za tydzień 5 paź · wypełniona 7 paź, 2 dni po terminie". */
  readonly meta: string;
}

export interface ReviewQueueView {
  readonly rows: readonly ReviewRow[];
  /** The backend's own window. Read off the response; see the header. */
  readonly windowDays: number;
  readonly truncated: boolean;
}

export interface ReviewQueueDeps {
  readonly t: TranslateKey;
  readonly locale: string;
}

/** Three, which is what a row has room for and what the backend already sends. */
const SUMMARY_ANSWERS = 3;


/**
 * The summary line — „Sen 4 · Energia 4 · Waga 78,4".
 *
 * At most three answers, joined with the canon's middle dot. An entry with nothing to print is
 * skipped rather than drawn as a dangling label: the backend leaves media, body measurements and
 * the goal builder out of the preview precisely because they have no one-line rendering, and an
 * entry that somehow arrives without its `text` is the same fact wearing a different shape.
 *
 * ── The bare values were not a summary, they were three numbers ──────────────────────────────
 * It joined `entry.text` alone, so a weekly check-in read „4 · 4 · 78.4" and an intake read
 * „1992-09-17 · Male · 182". The owner photographed both on 17 September 2026. Three things are
 * wrong with that line and each is fixed here:
 *
 * · **Nothing says which answer is which.** `SurveyAnswerSummaryItem.Key` is the CATALOG CODE for a
 *   standard question (`SurveyQuestionView.Key` — the code, or `custom:{guid}`), so
 *   `shared/lib/standardQuestionNames` names it in Polish exactly as the editor and the answer pane
 *   already do. A custom question's key misses every `qLabel` and falls back to the trainer's own
 *   wording, which is the only name it has.
 * · **The numbers and the dates are `CultureInfo.InvariantCulture`'s.** The backend renders
 *   `"0.##"` and an ISO date on purpose — they are values on the wire, not words — so the reader's
 *   own locale is applied here. „78.4" becomes „78,4" and „1992-09-17" becomes „17 wrz 1992".
 * · **A choice answer arrives as its ENUM member.** „Male", „Sedentary", „Advanced". The mobile
 *   locales carry the translations at `intakeSurvey.opt.<group>.<value>`, verbatim and shared, so
 *   they are read rather than reinvented. The group is not the code — the catalog says
 *   `activity_level` where the locale says `activity` — so the five that differ are a stated table
 *   and nothing else is guessed.
 *
 * No React, no DOM, no i18next import: `t` and the locale are parameters, like everywhere else here.
 */
export function summaryLine(
  summary: readonly SummaryItem[] | null | undefined,
  deps: ReviewQueueDeps,
): string {
  const number = new Intl.NumberFormat(deps.locale, { maximumFractionDigits: 2 });
  // A cycle-free civil date, formatted in UTC for the reason `whenLabel` states: read through
  // `new Date` alone it is UTC midnight and renders a day early west of Greenwich.
  const date = new Intl.DateTimeFormat(deps.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (summary ?? [])
    .flatMap((entry) => {
      const text = entry.text?.trim() ?? '';
      if (text === '') return [];
      const name = questionNames(deps.t, {
        code: entry.key,
        label: entry.label ?? '',
      }).shortLabel;
      return [`${name} ${summaryValue(text, entry.key ?? '', { number, date, t: deps.t })}`];
    })
    .slice(0, SUMMARY_ANSWERS)
    .join(' · ');
}

/** An ISO civil date on the wire — `SurveyAnswerSummary.Render` passes a `Date` answer's text through. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What `"0.##"` under the invariant culture can produce, and nothing else. */
const INVARIANT_NUMBER = /^-?\d+(\.\d+)?$/;

function summaryValue(
  text: string,
  key: string,
  deps: {
    readonly number: Intl.NumberFormat;
    readonly date: Intl.DateTimeFormat;
    readonly t: TranslateKey;
  },
): string {
  if (INVARIANT_NUMBER.test(text)) return deps.number.format(Number(text));
  if (ISO_DATE.test(text)) {
    const parsed = new Date(`${text}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) return deps.date.format(parsed);
  }

  if (!hasStandardOptionLabels(key)) return text;
  // A multi-choice answer arrives as `string.Join(", ", …)` of its members, so each one is named.
  return text
    .split(',')
    .map((member) => standardOptionLabel(deps.t, key, member.trim()))
    .join(', ');
}

function personName(item: QueueItem, t: TranslateKey): string {
  const name = item.person?.fullName?.trim() ?? '';
  return name === '' ? t('web:reports.queue.unnamedClient') : name;
}

function rowOf(item: QueueItem, deps: ReviewQueueDeps): ReviewRow | null {
  const { t, locale } = deps;

  // Nothing to open and nothing to mark. See the header.
  if (typeof item.assignmentId !== 'number' || item.assignmentId === 0) return null;
  const submittedAt = item.submittedAt ?? '';
  if (submittedAt === '') return null;
  const submitted = new Date(submittedAt);
  if (Number.isNaN(submitted.getTime())) return null;

  const late = item.occurrenceStatus === 'Late';
  const dates = datesOf(item.cycleDate ?? null, submitted, late, { t, locale });

  return {
    assignmentId: item.assignmentId,
    customerId: item.customerId ?? 0,
    name: personName(item, t),
    initials: item.person?.initials?.trim() ?? '',
    avatarUrl: item.person?.avatarUrl ?? null,
    templateTitle: item.templateTitle ?? '',
    formShort: t(
      item.kind === 'Intake' ? 'web:reports.queue.form.intake' : 'web:reports.queue.form.checkIn',
    ),
    summary: summaryLine(item.summary, deps),
    ...dates,
    late,
  };
}

/** The row's day, its lateness in words, and the pane's meta line. */
function datesOf(
  cycleDate: string | null,
  submitted: Date,
  late: boolean,
  deps: ReviewQueueDeps,
): Pick<ReviewRow, 'when' | 'lateness' | 'onTime' | 'meta'> {
  const { t, locale } = deps;
  // A cycle date is a calendar day with no zone; through `new Date` alone it parses as UTC midnight
  // and renders a day early west of Greenwich, so it is read verbatim and formatted in UTC.
  const civil = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const local = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const cycle = cycleDate === null ? null : new Date(`${cycleDate}T00:00:00Z`);
  const filled = local.format(submitted);

  if (cycle === null || Number.isNaN(cycle.getTime())) {
    // An intake: no cycle, so no week and no term to be late against.
    return {
      when: filled,
      lateness: null,
      onTime: false,
      meta: t('web:reports.queue.metaIntake', { filled }),
    };
  }

  const week = civil.format(cycle);
  // The one lateness rule, shared with the client's Ankiety pane (`features/survey-report`).
  const lateness =
    late && cycleDate !== null ? latenessLabel(daysLate(cycleDate, submitted), t) : null;
  return {
    when: week,
    lateness,
    onTime: !late,
    meta:
      lateness === null
        ? t('web:reports.queue.meta', { week, filled })
        : t('web:reports.queue.metaLate', { week, filled, lateness }),
  };
}

/** The queue, as the list draws it. */
export function reviewQueue(
  response: QueueResponse | null | undefined,
  deps: ReviewQueueDeps,
): ReviewQueueView {
  return {
    rows: (response?.items ?? [])
      .map((item) => rowOf(item, deps))
      .filter((row): row is ReviewRow => row !== null),
    windowDays: response?.windowDays ?? 0,
    truncated: response?.isTruncated === true,
  };
}

/**
 * The sentence under the list.
 *
 * `null` while the read has not answered — a window of zero days is not a fact about this screen,
 * and stating one would be inventing the number the header refuses to hardcode.
 */
export function windowSentence(view: ReviewQueueView, t: TranslateKey): string | null {
  return view.windowDays <= 0 ? null : t('web:reports.queue.window', { count: view.windowDays });
}
